import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import ChartTopicRefinement from 'components/ChartTopicRefinement/ChartTopicRefinement';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import DeactivationModal from 'components/DeactivationModals/DeactivationModal';
import EmptyTaskManagement from 'components/EmptyTaskManagement/EmptyTaskManagement';
import RefinementDisclosuresModal from 'components/RefinementDisclosuresModal/RefinementDisclosuresModal';
import RefinementQuestionsModal from 'components/RefinementQuestionsModal/RefinementQuestionsModal';
import TitleTableWrapper from 'components/TitleTableWrapper/TitleTableWrapper';
import { TOPIC_REFINEMENT_CELLS } from 'constants/dataSets';
import { MODAL_REFINEMENT_QUESTIONS } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';

import ItemsTable from './components/ItemsTable/ItemsTable';

import type IMATopicRefinement from './interfaces/IMATopicRefinement';

import styles from './scss/MATopicRefinement.module.scss';

function MATopicRefinementView({
  isEmptyList, loading, handleCancel, handleNext, handlePrevious,
  onLoadMore, hasMore, handleUpdateData, disableButtonUpdate,
  handleToggleTopicRefinement, items, topicRefinementII,
  chartItems, isFetchingChart, selectedItemId, setSelectedItemId,
  handleBackToMainChart, handleToItem, IROIdentification, vcOptions,
  valueSelector, setValueSelector, openModalRefused, onRejectModal, onSubmitModal,
  currentType, handleCloseModalRefinementQuestions,
  handleOpenModalRefinementQuestions, currentGroupId, recordId, currentGroupType, disableToggles,
}: IMATopicRefinement) {
  const { t } = useTranslation();
  return (
    <div className={styles.root}>
      <div className={styles.rootContainer}>
        <div className={styles.wrapper}>
          {IROIdentification && !isFetchingChart
            ? (
              <ChartTopicRefinement
                chartItems={chartItems}
                selectedItemId={selectedItemId}
                setSelectedItemId={setSelectedItemId}
                handleBackToMainChart={handleBackToMainChart}
                vcOptions={vcOptions}
                valueSelector={valueSelector}
                setValueSelector={setValueSelector}
              />
            )
            : null}
          {(isFetchingChart && topicRefinementII)
            || (isFetchingChart && IROIdentification)
            ? <ContentLoader title={t('materialityAssessment.topicRefinement')} className={styles.loader} />
            : null}
          <TitleTableWrapper
            title={t('materialityAssessment.initialCandidateTopics')}
            buttonTitle={t('materialityAssessment.updateTopicIdentification')}
            classNameTitle={styles.titlePage}
            onButtonClick={handleUpdateData}
            buttonClassName={styles.button}
            disableButton={disableButtonUpdate}
            showButton
            vcOptions={vcOptions}
            valueSelector={valueSelector}
            setValueSelector={setValueSelector}
            IROIdentification={IROIdentification}
          />
          <div className={styles.contentWrapper}>
            {loading ? (<ContentLoader title={t('materialityAssessment.topicRefinement')} />) : null}
            {isEmptyList && !loading ? (
              <EmptyTaskManagement
                title={t('materialityAssessment.emptyTopicRefinement')}
                checkIcon
              />
            ) : null}
            {!isEmptyList && !loading ? (
              <div className={styles.tableWrapper}>
                {items.length > 0 && (
                  <ItemsTable
                    items={items}
                    headCells={TOPIC_REFINEMENT_CELLS}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoadingMore={loading}
                    topicRefinementII={topicRefinementII}
                    IROIdentification={IROIdentification}
                    handleToggle={handleToggleTopicRefinement}
                    handleToItem={handleToItem}
                    handleOpenModalRefinementQuestions={handleOpenModalRefinementQuestions}
                    currentGroupType={currentGroupType}
                    currentType={currentType}
                    currentGroupId={currentGroupId}
                    handleCloseModalRefinementQuestions={handleCloseModalRefinementQuestions}
                    isInfoTable
                    disableToggle={disableToggles}
                  />
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleCancel}
          />
          <div className={styles.rightButtons}>
            <Button
              title={t('common.prev')}
              onClick={handlePrevious}
              variant={BUTTON_VARIANT.cancel}
              className={styles.prevButton}
            />
            <Button
              title={t('common.next')}
              onClick={handleNext}
              className={styles.button}
            />
          </div>
        </div>
      </div>
      <DeactivationModal
        open={openModalRefused}
        title={t('materialityAssessment.specifyReasonForDecision')}
        placeholder={t('materialityAssessment.typeYourMessageHere')}
        className={styles.deactivationModal}
        onReject={onRejectModal}
        onSubmit={onSubmitModal}
        hideLabel
        isLoading={false}
        offValidation
        disabledConfirmBtn
      />
      <RefinementQuestionsModal
        open={currentType === MODAL_REFINEMENT_QUESTIONS.QUESTIONS}
        onClose={handleCloseModalRefinementQuestions}
        currentGroupId={currentGroupId}
        recordId={recordId}
        title={t('materialityAssessment.refinementQuestions')}
      />
      <RefinementDisclosuresModal
        open={currentType === MODAL_REFINEMENT_QUESTIONS.DISCLOSURES}
        onClose={handleCloseModalRefinementQuestions}
        currentGroupId={currentGroupId}
        recordId={recordId}
        title={t('materialityAssessment.refinementDisclosures')}
      />
    </div>
  );
}

export default MATopicRefinementView;
